import { notifyManager, useQueryClient } from '@tanstack/react-query';
import { produce } from 'immer';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { extractErrorMessage } from '@/api/extractErrorMessage';
import useDeleteProposalLineItem from '@/api/mutations/proposalLineItem/useDeleteProposalLineItem';
import useUpdateProposalLineItem from '@/api/mutations/proposalLineItem/useUpdateProposalLineItem';
import useUpdateProposalLineItemMaterials from '@/api/mutations/proposalLineItem/useUpdateProposalLineItemMaterials';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import useGetProposalLineItem from '@/api/queries/proposalLineItems/useGetProposalLineItem';
import {
  GetProposalLineItemQuery,
  GetProposalQuery,
  UpdateProposalLineItemMutation,
} from '@/gql/graphql';
import { TFormState } from '@/ui/components/addEditProposalLineItem/formState';
import {
  derivePriceInput,
  diffLinkedMaterials,
} from '@/ui/components/addEditProposalLineItem/lineItemSubmit';

import proposalLineItemKeys from '@/api/queries/proposalLineItems/keyFactory';
import * as jobContentActions from '@payaca/store/jobContent/jobContentActions';

type UpdatedProposalLineItem =
  UpdateProposalLineItemMutation['updateProposalLineItem'];

type ProposalSection = GetProposalQuery['proposal']['sections'][number];
type ProposalGroup = Extract<
  ProposalSection,
  { __typename: 'ProposalItemGroupSection' }
>['groups'][number];
type ProposalGroupChild = ProposalGroup['children'][number];
type ProposalGroupLineItemNode = Extract<
  ProposalGroupChild,
  { __typename: 'ProposalGroupLineItem' }
>;

/**
 * Writes a saved Line Item's displayed fields (reference, description,
 * quantity, unit price and roll-up totals) back into the matching node of the
 * cached Proposal section tree, so the Builder reflects an autosave without a
 * full proposal refetch. Walks the two group levels the proposal query unrolls
 * to (the server caps group nesting at that depth).
 */
const patchProposalCacheLineItem = (
  data: GetProposalQuery,
  updated: UpdatedProposalLineItem
): GetProposalQuery =>
  produce(data, (draft) => {
    const patchNode = (node: ProposalGroupLineItemNode) => {
      // Match by publicId as well as id: an optimistically created row holds
      // its publicId as a placeholder id until the reconciling refetch lands.
      if (
        node.proposalLineItem.id !== updated.id &&
        node.proposalLineItem.publicId !== updated.publicId
      ) {
        return;
      }
      const { proposalLineItem } = node;
      proposalLineItem.id = updated.id;
      proposalLineItem.internalReference = updated.internalReference;
      proposalLineItem.description = updated.description;
      proposalLineItem.quantity = updated.quantity;
      proposalLineItem.price.unitPriceExcTax = updated.price.unitPriceExcTax;
      proposalLineItem.totals.totalValue = updated.totals.totalValue;
      proposalLineItem.totals.discountValue = updated.totals.discountValue;
    };

    for (const section of draft.proposal.sections) {
      if (section.__typename !== 'ProposalItemGroupSection') continue;
      for (const child of section.groups.flatMap((group) => group.children)) {
        if (child.__typename === 'ProposalGroupLineItem') {
          patchNode(child);
          continue;
        }
        for (const grandChild of child.children) {
          if (grandChild.__typename === 'ProposalGroupLineItem') {
            patchNode(grandChild);
          }
        }
      }
    }
  });

type UseProposalLineItemEditorOptions = {
  proposalLineItemId?: string;
  /**
   * When provided, the GraphQL Proposal query is kept in sync after a
   * successful save or delete so consumers reading from it (e.g. the Proposal
   * Builder) reflect the change. Legacy editors backed by the redux store omit
   * this and refetch via `onSaveSuccess` / `onRemoveSuccess` instead.
   */
  proposalId?: string;
  /**
   * How a successful autosave updates the cached Proposal query.
   * `'invalidate'` (default) refetches the whole proposal. `'hydrate'` patches
   * the saved Line Item into the cached section tree from the mutation result,
   * avoiding a refetch on every debounced save — used by the inline Builder
   * editor.
   */
  proposalCacheStrategy?: 'invalidate' | 'hydrate';
  onSaveSuccess?: () => void;
  onRemoveSuccess?: () => void;
};

/**
 * Data + mutation wiring for editing a single Proposal Line Item. Composes the
 * line item query, the update / materials / delete mutations and the thumbnail
 * attachment actions into the `onSubmit` / `onThumbnailDelete` / `onDelete`
 * handlers consumed by `AddEditProposalLineItemContent`.
 */
const useProposalLineItemEditor = (
  options: UseProposalLineItemEditorOptions
) => {
  const {
    proposalLineItemId,
    proposalId,
    proposalCacheStrategy = 'invalidate',
    onSaveSuccess,
    onRemoveSuccess,
  } = options;

  const dispatch = useDispatch();
  const queryClient = useQueryClient();

  const [isSavingThumbnail, setIsSavingThumbnail] = useState(false);

  const { data, error, isPending, isError } = useGetProposalLineItem(
    {
      proposalLineItemId,
    },
    {
      refetchOnReconnect: false,
      refetchOnWindowFocus: false,
    }
  );

  // The Line Item has been deleted since the client learned of its id — for
  // example by another user, or in another tab.
  const isNotFound = extractErrorMessage(error).code === 'NOT_FOUND';

  // The legacy redux attachment actions require the numeric row id, but the
  // route may address the item by publicId; the fetched item carries both.
  const numericProposalLineItemId = data?.proposalLineItem.id;

  const saveMutationKey = ['saveProposalLineItem', proposalLineItemId];

  const { mutateAsync: updateProposalLineItemMutation } =
    useUpdateProposalLineItem({
      proposalId,
      options: {
        mutationKey: saveMutationKey,
        onSuccess: (data) => {
          if (!proposalLineItemId) {
            return;
          }

          // One notify flush, so the editor renders a save once.
          notifyManager.batch(() => {
            queryClient.setQueryData<GetProposalLineItemQuery>(
              proposalLineItemKeys.proposalLineItem(proposalLineItemId),
              { proposalLineItem: data.updateProposalLineItem }
            );
            if (proposalCacheStrategy === 'hydrate') {
              hydrateProposal(data.updateProposalLineItem);
            }
          });
        },
      },
    });
  const { mutateAsync: updateProposalLineItemMaterialsMutation } =
    useUpdateProposalLineItemMaterials({
      mutationKey: saveMutationKey,
      onSuccess: (data) => {
        if (!proposalLineItemId) {
          return;
        }

        queryClient.setQueryData<GetProposalLineItemQuery>(
          proposalLineItemKeys.proposalLineItem(proposalLineItemId),
          { proposalLineItem: data.updateProposalLineItemMaterials }
        );
      },
    });
  const { mutateAsync: deleteProposalLineItemMutation } =
    useDeleteProposalLineItem();

  const invalidateProposal = () => {
    if (!proposalId) return;
    return queryClient.invalidateQueries({
      queryKey: proposalKeys.proposal(proposalId),
    });
  };

  const hydrateProposal = (updated: UpdatedProposalLineItem) => {
    if (!proposalId) return;
    queryClient.setQueryData<GetProposalQuery>(
      proposalKeys.proposal(proposalId),
      (old) => (old ? patchProposalCacheLineItem(old, updated) : old)
    );
    // Only the server can re-render the document, so patching the cache in
    // place leaves the preview showing the line item as it was.
    void queryClient.invalidateQueries({
      queryKey: proposalKeys.proposalDocument(proposalId),
    });
  };

  const onSubmit = async (state: TFormState) => {
    if (!proposalLineItemId) {
      return;
    }

    const isUploadingThumbnail =
      !!state.thumbnail?.file && !!numericProposalLineItemId;
    if (isUploadingThumbnail) {
      setIsSavingThumbnail(true);
    }

    try {
      // Make sure that the thumbnail is uploaded before we update the line item
      // So that if the user wishes to update the original line item, the new thumbnail is attached too
      if (state.thumbnail?.file && numericProposalLineItemId) {
        await new Promise<void>((resolve, reject) => {
          dispatch(
            jobContentActions.requestAddAttachmentToJobLineItem(
              +numericProposalLineItemId,
              {
                file: state.thumbnail!.file!,
                fileName: state.thumbnail!.file!.name,
              },
              false,
              (error?: Error) => {
                if (error) {
                  reject(error);
                } else {
                  resolve();
                }
              }
            )
          );
        });
      }

      const updateOriginal =
        state.canUpdateOriginalLineItem && state.updateOriginalLineItem;

      const { changes, unchanged } = diffLinkedMaterials(
        data?.proposalLineItem.materials ?? [],
        state.materials
      );

      // Skip the materials write when the linked-material set is unchanged
      // since the last save — unless we're also pushing it onto the original
      // catalogue Item, where the write is what propagates the materials.
      if (updateOriginal || !unchanged) {
        await updateProposalLineItemMaterialsMutation({
          proposalLineItemId,
          proposalLineItemMaterials: changes,
          updateLineItem: updateOriginal,
        });
      }

      await updateProposalLineItemMutation({
        id: proposalLineItemId,
        internalReference: state.reference,
        description: state.description,
        selection: {
          type: state.selectionType,
        },
        quantity: state.quantity,
        price: {
          ...derivePriceInput(state),
          taxRateId: state.taxRateId,
          cisApplies: state.cis === 'yes',
          discount: state.hasDiscount
            ? {
                percentage: state.discountPercentage || 0,
                description: state.discountDescription,
              }
            : null,
        },
        updateLineItem: updateOriginal,
      });

      if (proposalCacheStrategy === 'invalidate') {
        await invalidateProposal();
      }
      onSaveSuccess?.();
    } finally {
      if (isUploadingThumbnail) {
        setIsSavingThumbnail(false);
      }
    }
  };

  const onThumbnailDelete = async (thumbnailId: string) => {
    if (!numericProposalLineItemId) {
      return;
    }

    await new Promise<void>((resolve, reject) => {
      dispatch(
        jobContentActions.requestRemoveAttachmentFromJobLineItem(
          +numericProposalLineItemId,
          thumbnailId,
          false,
          (error?: Error) => {
            if (error) {
              reject(error);
            } else {
              resolve();
            }
          }
        )
      );
    });
  };

  const onDelete = async () => {
    if (!proposalLineItemId) {
      return;
    }

    await deleteProposalLineItemMutation({
      proposalLineItemId,
    });

    await invalidateProposal();
    onRemoveSuccess?.();
  };

  return {
    proposalLineItemData: data?.proposalLineItem,
    isLoading: !!proposalLineItemId && isPending,
    isNotFound,
    hasError: isError && !isNotFound,
    isSavingThumbnail,
    saveMutationKey,
    onSubmit,
    onThumbnailDelete,
    onDelete,
  };
};

export default useProposalLineItemEditor;
